import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import type { ErrorSummary } from "@/lib/types";
import EmailModeBanner from "@/components/EmailModeBanner";
import {
  FileText,
  LogOut,
  LayoutDashboard,
  Kanban,
  Users,
  GraduationCap,
  BookOpen,
  FileCheck,
  BarChart3,
  Mail,
  Settings,
  Workflow,
  TriangleAlert,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/pipeline", label: "Pipeline", icon: Kanban },
  { to: "/prospects", label: "Prospects", icon: Users },
  { to: "/emails", label: "Emails", icon: Mail },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/approvals", label: "Approvals", icon: FileCheck },
  { to: "/knowledge", label: "Knowledge", icon: BookOpen },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/training", label: "Training", icon: GraduationCap },
  { to: "/strategies", label: "Strategies", icon: Workflow },
  { to: "/errors", label: "System", icon: TriangleAlert },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Poll the unacknowledged-error count so operators see a badge without
  // opening the System page (deliverable 3.12).
  const errorSummaryQ = useQuery({
    queryKey: ["errors-summary"],
    queryFn: () => api.get<ErrorSummary>("/api/v1/errors/summary"),
    refetchInterval: 60_000,
  });
  const openErrors = errorSummaryQ.data?.unacknowledged ?? 0;

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="grid grid-cols-[14rem_1fr] grid-rows-[auto_1fr] h-full">
      <aside className="row-span-2 border-r border-[var(--border)] bg-[var(--bg-card)] flex flex-col">
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <Link to="/dashboard" className="font-semibold tracking-tight text-lg">
            DealFlow
          </Link>
          <div className="text-xs text-[var(--fg-muted)]">Agent CRM</div>
        </div>
        <nav className="p-2 flex-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
                  isActive
                    ? "bg-zinc-100 text-zinc-900 font-medium"
                    : "text-zinc-700 hover:bg-zinc-50",
                )
              }
            >
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {to === "/errors" && openErrors > 0 ? (
                <span className="inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold min-w-[1.25rem] h-5 px-1">
                  {openErrors}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[var(--border)] text-xs text-[var(--fg-muted)]">
          <div className="truncate">{user?.email}</div>
          <div className="truncate">{user?.roles.join(", ")}</div>
        </div>
      </aside>

      <header className="flex items-center justify-end px-6 py-3 border-b border-[var(--border)] bg-[var(--bg-card)]">
        <button
          onClick={handleLogout}
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
        >
          <LogOut size={16} /> Sign out
        </button>
      </header>

      <main className="overflow-auto">
        {/* Above the page content, not inside it: the deployment's send mode
            applies to every screen, and a banner that scrolls away is a banner
            that is not there when someone clicks approve. */}
        <EmailModeBanner />
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
